import { useEffect } from "react";

/**
 * Releases a stuck `pointer-events: none` from <body>.
 *
 * Radix locks the page behind a modal by setting `body.style.pointerEvents =
 * "none"`, remembering the previous value, and writing that value back when the
 * layer unmounts. The remembered value is module-level state, so it is only
 * correct while every Radix primitive on the page shares one copy of
 * @radix-ui/react-dismissable-layer.
 *
 * When it isn't — the app once had two, and opening Settings from the sidebar's
 * dropdown made the dialog's copy capture the *menu's* "none" as the value to
 * restore — closing the dialog wrote `none` back permanently. The whole app went
 * dead: nothing clickable, nothing typable, only a refresh recovered it. The
 * duplicate is gone (package.json pins @radix-ui/react-dialog to the generation
 * every other Radix package here uses), but this is a bad enough failure that it
 * should not depend solely on a dependency tree staying tidy.
 *
 * So: whenever the DOM says no modal layer is present, the lock must not be. The
 * check runs on the mutations that could have left it behind rather than on a
 * timer, and does nothing at all in the normal case.
 */
export function usePointerEventsGuard(): void {
  useEffect(() => {
    const body = document.body;

    /**
     * Any Radix layer that legitimately holds the lock right now.
     *
     * Only `data-state="open"` counts. A layer Radix has marked closed but not
     * yet removed is not entitled to keep the page locked — and a closed layer
     * that never gets removed at all (Radix waits for an `animationend` that a
     * throttled tab or a cancelled animation may never deliver) is precisely a
     * case worth rescuing rather than deferring to.
     */
    const modalIsOpen = () =>
      document.querySelector(
        '[role="dialog"][data-state="open"],' +
          '[role="alertdialog"][data-state="open"],' +
          '[role="menu"][data-state="open"],' +
          '[role="listbox"][data-state="open"],' +
          '[data-radix-popper-content-wrapper] [data-state="open"]',
      ) !== null;

    const release = () => {
      if (body.style.pointerEvents !== "none") return;
      if (modalIsOpen()) return;
      body.style.removeProperty("pointer-events");
    };

    // Three things can leave the page locked with nothing open: the lock being
    // written (`style` on <body>), the last layer being removed (`childList`),
    // and a layer flipping to closed without ever being removed (`data-state`).
    // The last one is why this watches the whole tree rather than just <body>.
    //
    // Breadth is affordable because the callback's first line is a string
    // comparison that is false on every ordinary mutation in the app; the
    // querySelector only runs while the page is actually locked.
    const observer = new MutationObserver(release);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style", "data-state"],
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);
}
